// frontend/src/components/Portfolio/EnvironmentalRiskBreakdown.tsx
// âœ… UPDATED: Now uses centralized formatters.ts for all number formatting

import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Collapse,
  Button,
  Grid,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FactoryIcon from '@mui/icons-material/Factory';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Settings as TuneIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// âœ… IMPORT CENTRALIZED FORMATTERS
import { 
  formatNumberWithCommas, 
  formatWeight,
  formatDecimal 
} from '../../utils/formatters';

// âŒ REMOVED: Duplicate safeToFixed function - now using formatters.ts

type EnvSortField = 'envRisk' | 'superfundSites' | 'epaFacilities' | 'toxicRelease';
type SortOrder = 'asc' | 'desc';

interface EnvironmentalRiskBreakdownProps {
  portfolio: any;
}

// Updated to use 1-10 scale - Clean theme colors
const getEnvRiskColor = (score: number): string => {
  if (score > 6) return '#FEE2E2'; // Light red background
  if (score > 3) return '#FEF3C7'; // Light amber background
  return '#D1FAE5'; // Light green background
};

const getEnvRiskTextColor = (score: number): string => {
  if (score > 6) return '#DC2626'; // Red text
  if (score > 3) return '#D97706'; // Amber text
  return '#059669'; // Green text
};

export default function EnvironmentalRiskBreakdown({ portfolio }: EnvironmentalRiskBreakdownProps) {
  const [showDetailedEnvMetrics, setShowDetailedEnvMetrics] = useState(false);
  const [envSortField, setEnvSortField] = useState<EnvSortField>('envRisk');
  const [envSortOrder, setEnvSortOrder] = useState<SortOrder>('desc');
  
  // Modern filter state - default to 'all' to show all items initially
  const [filterMode, setFilterMode] = useState<'all' | 'medium' | 'high'>('all');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [customThreshold, setCustomThreshold] = useState(6);

  // Get actual threshold based on filter mode
  const getThreshold = () => {
    if (showAdvancedFilter) return customThreshold;
    switch (filterMode) {
      case 'high':
        return 7;
      case 'medium':
        return 4;
      case 'all':
        return 0;
      default:
        return 4;
    }
  };

  const envThreshold = getThreshold();

  // Process environmental data
  const envItems = portfolio?.items?.map((item: any) => ({
    postal_code: item.postal_code || item.zip_code || 'N/A',
    zip_code: item.zip_code || item.postal_code || 'N/A',
    city: item.city || 'N/A',
    state: item.state || 'N/A',
    envRisk: item.environmental_risk_score || item.envRisk || 0,
    superfundSites: item.superfund_sites || 0,
    epaFacilities: item.epa_facilities || 0,
    toxicRelease: item.toxic_release || 0,
    toxicAir: item.toxic_air_percentile || 0,
    drinkingWater: item.drinking_water_percentile || 0,
  })) || [];

  // Filter items based on environmental threshold
  // Use >= when threshold is 0 (filterMode 'all') to include all items
  const filteredEnvItems = envItems.filter((item: any) => {
    const risk = item.envRisk || 0;
    return envThreshold === 0 ? true : risk > envThreshold;
  });

  if (!portfolio || !portfolio.items || portfolio.items.length === 0) {
    return (
      <Paper sx={{ 
        p: 3, 
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No portfolio data available. Please select or create a portfolio.
        </Alert>
      </Paper>
    );
  }

  const handleEnvSort = (field: EnvSortField) => {
    const isAsc = envSortField === field && envSortOrder === 'asc';
    setEnvSortOrder(isAsc ? 'desc' : 'asc');
    setEnvSortField(field);
  };

  const getSortedEnvItems = () => {
    return [...filteredEnvItems].sort((a, b) => {
      const aValue = a[envSortField] ?? 0;
      const bValue = b[envSortField] ?? 0;
      
      if (envSortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Calculate statistics - Using 1-10 scale for Environmental Risk Score
  const avgEnvRisk = filteredEnvItems.length > 0
    ? filteredEnvItems.reduce((sum: number, item: any) => sum + (parseFloat(String(item.envRisk)) || 0), 0) / filteredEnvItems.length
    : 0;

  const totalSuperfundSites = filteredEnvItems.reduce((sum: number, item: any) => 
    sum + (parseInt(String(item.superfundSites)) || 0), 0);
  
  const totalEPAFacilities = filteredEnvItems.reduce((sum: number, item: any) => 
    sum + (parseInt(String(item.epaFacilities)) || 0), 0);
  
  const totalToxicRelease = filteredEnvItems.reduce((sum: number, item: any) => 
    sum + (parseFloat(String(item.toxicRelease)) || 0), 0);

  // Risk distribution using 1-10 scale (same as Climate Risk)
  const highRiskCount = filteredEnvItems.filter((item: any) => (item.envRisk || 0) > 6).length;
  const mediumRiskCount = filteredEnvItems.filter((item: any) => (item.envRisk || 0) > 3 && (item.envRisk || 0) <= 6).length;
  const lowRiskCount = filteredEnvItems.filter((item: any) => (item.envRisk || 0) <= 3 && (item.envRisk || 0) > 0).length;

  // âœ… UPDATED: CSV Export Function with formatters
  const exportEnvironmentalData = () => {
    let csv = 'Postal Code,City,State,Env Risk Score,Superfund Sites,EPA Facilities,Toxic Release (lbs)\n';
    
    getSortedEnvItems().forEach((item: any) => {
      csv += `${item.postal_code},${item.city},${item.state},${formatDecimal(item.envRisk, 1)},${item.superfundSites},${item.epaFacilities},${formatNumberWithCommas(item.toxicRelease)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `environmental_risk_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Paper sx={{ 
      p: 3, 
      backgroundColor: '#FFFFFF',
      border: '1px solid #E5E7EB',
      borderRadius: 2,
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FactoryIcon sx={{ fontSize: 32, color: '#2563EB' }} />
          <Box>
            <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
              Environmental Risk Analysis
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              EPA environmental indicators and pollution data
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportEnvironmentalData}
          sx={{ 
            borderColor: '#E5E7EB',
            color: '#374151',
            '&:hover': {
              borderColor: '#2563EB',
              backgroundColor: '#EFF6FF'
            }
          }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filter Controls */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
            Risk Filter:
          </Typography>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(e, newMode) => newMode && setFilterMode(newMode)}
            size="small"
          >
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>
              All Locations
            </ToggleButton>
            <ToggleButton value="medium" sx={{ textTransform: 'none' }}>
              Medium+ Risk (&gt;3)
            </ToggleButton>
            <ToggleButton value="high" sx={{ textTransform: 'none' }}>
              High Risk (&gt;6)
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Button
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
            sx={{ textTransform: 'none' }}
          >
            Advanced
          </Button>
        </Box>

        <Collapse in={showAdvancedFilter}>
          <Box sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: 1, border: '1px solid #E5E7EB' }}>
            <Typography variant="body2" sx={{ color: '#374151', mb: 2 }}>
              Custom Threshold: {customThreshold}
            </Typography>
            <Slider
              value={customThreshold}
              onChange={(e, newValue) => setCustomThreshold(newValue as number)}
              min={0}
              max={10}
              step={0.5}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
        </Collapse>

        <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 1 }}>
          Showing {filteredEnvItems.length} of {envItems.length} locations
        </Typography>
      </Box>

      {filteredEnvItems.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No locations found with environmental risk above {envThreshold}. Try adjusting your threshold.
        </Alert>
      )}

      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InfoOutlinedIcon sx={{ color: '#2563EB', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                  Avg Env Risk
                </Typography>
              </Box>
              {/* âœ… UPDATED: Using formatDecimal */}
              <Typography variant="h4" sx={{ 
                color: getEnvRiskTextColor(avgEnvRisk),
                fontWeight: 600
              }}>
                {formatDecimal(avgEnvRisk, 1)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Scale: 1-10
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#DC2626', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                  Superfund Sites
                </Typography>
              </Box>
              {/* âœ… UPDATED: Using formatNumberWithCommas */}
              <Typography variant="h4" sx={{ color: '#DC2626', fontWeight: 600 }}>
                {formatNumberWithCommas(totalSuperfundSites)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Hazardous sites
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FactoryIcon sx={{ color: '#D97706', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                  EPA Facilities
                </Typography>
              </Box>
              {/* âœ… UPDATED: Using formatNumberWithCommas */}
              <Typography variant="h4" sx={{ color: '#D97706', fontWeight: 600 }}>
                {formatNumberWithCommas(totalEPAFacilities)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Regulated sites
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ 
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DeleteIcon sx={{ color: '#10B981', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                  Toxic Release
                </Typography>
              </Box>
              {/* âœ… UPDATED: Using formatWeight with lbs/year */}
              <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 600 }}>
                {formatWeight(totalToxicRelease, 'lbs', false)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                per year
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Risk Distribution Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            backgroundColor: '#FEE2E2',
            border: '1px solid #FCA5A5',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#DC2626' }} />
                <Typography variant="body2" sx={{ color: '#DC2626', fontWeight: 600 }}>
                  High Risk (&gt;6)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#DC2626', fontWeight: 600 }}>
                {highRiskCount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#991B1B' }}>
                {((highRiskCount / (filteredEnvItems.length || 1)) * 100).toFixed(0)}% of filtered
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            backgroundColor: '#FEF3C7',
            border: '1px solid #FCD34D',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#D97706' }} />
                <Typography variant="body2" sx={{ color: '#D97706', fontWeight: 600 }}>
                  Medium Risk (3-6)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#D97706', fontWeight: 600 }}>
                {mediumRiskCount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#92400E' }}>
                {((mediumRiskCount / (filteredEnvItems.length || 1)) * 100).toFixed(0)}% of filtered
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            backgroundColor: '#D1FAE5',
            border: '1px solid #6EE7B7',
            boxShadow: 'none'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon sx={{ color: '#059669' }} />
                <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600 }}>
                  Low Risk (&lt;3)
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#059669', fontWeight: 600 }}>
                {lowRiskCount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#065F46' }}>
                {((lowRiskCount / (filteredEnvItems.length || 1)) * 100).toFixed(0)}% of filtered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      <TableContainer sx={{ 
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        maxHeight: 600,
        overflow: 'auto'
      }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#F9FAFB', fontWeight: 600, color: '#111827' }}>
                Location
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#F9FAFB' }}>
                <TableSortLabel
                  active={envSortField === 'envRisk'}
                  direction={envSortOrder}
                  onClick={() => handleEnvSort('envRisk')}
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Env Risk
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#F9FAFB' }}>
                <TableSortLabel
                  active={envSortField === 'superfundSites'}
                  direction={envSortOrder}
                  onClick={() => handleEnvSort('superfundSites')}
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Superfund
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#F9FAFB' }}>
                <TableSortLabel
                  active={envSortField === 'epaFacilities'}
                  direction={envSortOrder}
                  onClick={() => handleEnvSort('epaFacilities')}
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  EPA Sites
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#F9FAFB' }}>
                <TableSortLabel
                  active={envSortField === 'toxicRelease'}
                  direction={envSortOrder}
                  onClick={() => handleEnvSort('toxicRelease')}
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  Toxic Release (lbs)
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getSortedEnvItems().map((item: any, index: number) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:hover': { backgroundColor: '#F9FAFB' },
                  backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA'
                }}
              >
                <TableCell sx={{ color: '#111827', fontWeight: 500 }}>
                  {item.city}, {item.state}
                  <Typography variant="caption" sx={{ display: 'block', color: '#9CA3AF' }}>
                    {item.postal_code}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {/* âœ… UPDATED: Using formatDecimal */}
                  <Chip
                    label={formatDecimal(item.envRisk, 1)}
                    size="small"
                    sx={{
                      backgroundColor: getEnvRiskColor(item.envRisk),
                      color: getEnvRiskTextColor(item.envRisk),
                      fontWeight: 600,
                      minWidth: '50px'
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ color: '#DC2626', fontWeight: 500 }}>
                  {item.superfundSites}
                </TableCell>
                <TableCell align="right" sx={{ color: '#D97706', fontWeight: 500 }}>
                  {item.epaFacilities}
                </TableCell>
                <TableCell align="right" sx={{ color: '#111827', fontWeight: 500 }}>
                  {/* âœ… UPDATED: Using formatNumberWithCommas */}
                  {formatNumberWithCommas(item.toxicRelease)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredEnvItems.length === 0 && (
        <Box sx={{ 
          p: 4, 
          textAlign: 'center',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          mt: 2
        }}>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            No locations match your current filter settings
          </Typography>
        </Box>
      )}
    </Paper>
  );
}